import React from 'react';

const CaseStudies: React.FC = () => {
  const caseStudies = [
    {
      id: 1,
      title: "Refinery Pipeline Inspection",
      client: "Reliance Industries",
      industry: "Oil & Gas",
      challenge: "Critical pipeline inspection in a high-pressure refinery environment with limited access and strict safety requirements.",
      solution: "Implemented advanced ultrasonic testing (UT) and phased array techniques with custom-designed access equipment for confined spaces.",
      results: "Identified 3 critical defects that could have led to catastrophic failure, enabling preventive maintenance and saving millions in potential damages.",
      technologies: ["Ultrasonic Testing", "Phased Array", "Custom Access Equipment"],
      duration: "2 weeks",
      team: "5 NDT specialists",
      image: "/Industry5.jpeg"
    },
    {
      id: 2,
      title: "Power Plant Turbine Blade Testing",
      client: "Tata Power",
      industry: "Power Generation",
      challenge: "Comprehensive testing of turbine blades in a 500MW power plant during scheduled maintenance shutdown.",
      solution: "Deployed eddy current testing (ECT) and magnetic particle testing (MPT) with specialized probes for complex blade geometries.",
      results: "Detected 12 surface cracks and 2 subsurface defects, allowing for timely replacement and preventing unplanned downtime.",
      technologies: ["Eddy Current Testing", "Magnetic Particle Testing", "Specialized Probes"],
      duration: "1 week",
      team: "4 NDT specialists",
      image: "/Industry2.jpeg"
    },
    {
      id: 3,
      title: "Aerospace Component Quality Control",
      client: "HAL (Hindustan Aeronautics Limited)",
      industry: "Aerospace",
      challenge: "High-precision testing of critical aerospace components with zero tolerance for defects and strict regulatory compliance.",
      solution: "Utilized advanced radiographic testing (RT) with digital imaging and liquid penetrant testing (LPT) for comprehensive surface and internal defect detection.",
      results: "Achieved 100% defect detection rate, ensuring all components met aerospace standards and regulatory requirements.",
      technologies: ["Digital Radiography", "Liquid Penetrant Testing", "Digital Imaging"],
      duration: "3 weeks",
      team: "6 NDT specialists",
      image: "/Industry3.jpeg"
    },
    {
      id: 4,
      title: "Bridge Infrastructure Assessment",
      client: "Larsen & Toubro",
      industry: "Infrastructure",
      challenge: "Structural integrity assessment of a major bridge with suspected corrosion and fatigue damage in critical load-bearing members.",
      solution: "Conducted comprehensive visual testing (VT), ultrasonic thickness measurement, and magnetic particle testing on all critical structural elements.",
      results: "Provided detailed structural assessment report enabling targeted repairs, extending bridge life by 15 years and ensuring public safety.",
      technologies: ["Visual Testing", "Ultrasonic Thickness", "Magnetic Particle Testing"],
      duration: "4 weeks",
      team: "8 NDT specialists",
      image: "/Industry4.jpeg"
    },
    {
      id: 5,
      title: "Marine Vessel Hull Inspection",
      client: "Cochin Shipyard",
      industry: "Marine",
      challenge: "Complete hull inspection of a large cargo vessel with complex weld joints and limited dry dock time.",
      solution: "Implemented time-of-flight diffraction (TOFD) and automated ultrasonic testing (AUT) for rapid and accurate weld inspection.",
      results: "Completed full hull inspection in 50% less time than traditional methods, identifying 8 weld defects requiring immediate attention.",
      technologies: ["TOFD", "Automated UT", "Weld Inspection"],
      duration: "2 weeks",
      team: "6 NDT specialists",
      image: "/Industry5.jpeg"
    },
    {
      id: 6,
      title: "Petrochemical Plant Vessel Testing",
      client: "ONGC",
      industry: "Petrochemicals",
      challenge: "Testing of high-pressure vessels in a petrochemical plant with hazardous environment and strict safety protocols.",
      solution: "Deployed remote visual inspection (RVI) and advanced ultrasonic testing with explosion-proof equipment and specialized safety procedures.",
      results: "Successfully identified 5 critical defects without any safety incidents, enabling planned maintenance and preventing potential environmental disaster.",
      technologies: ["Remote Visual Inspection", "Advanced UT", "Explosion-proof Equipment"],
      duration: "3 weeks",
      team: "7 NDT specialists",
      image: "/Industry6.jpeg"
    }
  ];

  const stats = [
    { number: "500+", label: "Projects Completed" },
    { number: "98%", label: "Success Rate" },
    { number: "50+", label: "Industries Served" },
    { number: "8+", label: "Years Experience" }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-avis-blue to-avis-dark text-white py-20">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">Case Studies</h1>
            <p className="text-xl lg:text-2xl text-blue-100 max-w-3xl mx-auto">
              Real-world examples of our NDT expertise in action
            </p>
          </div>
        </div>
      </section>

      {/* Statistics */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Track Record</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Proven results across diverse industries and challenging projects
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-avis-blue mb-2">{stat.number}</div>
                <div className="text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Featured Case Studies</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Detailed accounts of our most challenging and successful projects
            </p>
          </div>
          <div className="space-y-12">
            {caseStudies.map((study) => (
              <div key={study.id} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="p-8">
                    <div className="flex items-center mb-4">
                      <span className="bg-avis-blue text-white px-3 py-1 rounded-full text-sm font-semibold mr-3">
                        {study.industry}
                      </span>
                      <span className="text-gray-500 text-sm">{study.duration}</span>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">{study.title}</h3>
                    <div className="mb-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">Client: {study.client}</h4>
                      <p className="text-gray-600 mb-4">{study.team}</p>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Challenge:</h4>
                        <p className="text-gray-600">{study.challenge}</p>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Solution:</h4>
                        <p className="text-gray-600">{study.solution}</p>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Results:</h4>
                        <p className="text-gray-600">{study.results}</p>
                      </div>
                    </div>
                    <div className="mt-6">
                      <h4 className="font-semibold text-gray-900 mb-2">Technologies Used:</h4>
                      <div className="flex flex-wrap gap-2">
                        {study.technologies.map((tech, index) => (
                          <span key={index} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="relative">
                    <img
                      src={study.image}
                      alt={study.title}
                      className="w-full h-64 lg:h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = '/assets/Industry1.jpeg';
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Industries We Serve</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our expertise spans across multiple industries with proven results
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8">
            {[
              "Oil & Gas", "Power Generation", "Aerospace", "Infrastructure", 
              "Marine", "Petrochemicals", "Automotive", "Manufacturing", 
              "Steel & Metals", "Construction"
            ].map((industry, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-avis-blue rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <p className="text-sm font-medium text-gray-700">{industry}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-avis-blue text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Start Your Project?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Let our expertise work for your next challenging NDT project
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/quotation"
              className="bg-white text-avis-blue px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-50 transition-colors duration-200"
            >
              Get Your Quote
            </a>
            <a
              href="/services"
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-avis-blue transition-colors duration-200"
            >
              View Our Services
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudies;
